import { useAppDispatch, useAppSelector } from "App";
import { useEffect, useState } from "react";
import { DownloadFarmImagesApi } from "./DownloadFarmImagesApi";
import { AvailableImageType } from "./models";
import { BoolCheckboxField } from "components/FormComponents/BoolCheckbox/BoolCheckbox";
import Spinner from "react-spinkit";
import { ErrorMessage, Formik } from "formik";
import * as Yup from "yup";

type DownloadImagesForm = {
  image_types: {
    id: number;
    label: string;
    value: boolean;
  }[];
};

// If formik wasn't so out of data we could use `const validationSchema: Yup.ObjectSchema<DownloadImagesForm>`
const validationSchema = Yup.object({
  image_types: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.number(),
        label: Yup.string(),
        value: Yup.boolean(),
      })
    )
    .test({
      name: "one_selected",
      message: "Select at least one image type",
      test: images => !!images?.some(image => image.value),
    }),
});

type Image = {
  id: AvailableImageType["Id"];
  label: AvailableImageType["Type"];
  value: boolean;
};
interface DownloadFarmImagesProps {}

export const DownloadFarmImages: React.FC<DownloadFarmImagesProps> = () => {
  const selectedId = useAppSelector(
    state =>
      (state.rightPopOutReducer.rightPopOutId.selectedId as unknown) as
        | number
        | undefined
  );

  const dispatch = useAppDispatch();

  const handleToast = (message: string) => {
    dispatch({ type: "SHOW_TOAST", message: message });
    window.setTimeout(() => {
      dispatch({ type: "HIDE_TOAST" });
    }, 3000);
  };

  const [imageTypes, setImageTypes] = useState<Image[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const getImageOptions = async () => {
      setLoading(true);
      const response = await DownloadFarmImagesApi.getImageOptions();
      setLoading(false);
      if (response.data) {
        setImageTypes(
          response.data.list.map(imageType => ({
            id: imageType.Id,
            label: imageType.Type,
            value: false,
          }))
        );
        setError(null);
      } else {
        setError("Error fetching image options");
      }
    };
    if (selectedId) {
      // NOTE: Id is not used, but is required to block the `getImageOptions`
      // call from firing in the first place
      getImageOptions();
    }
    // Cleanup
    return () => {
      setImageTypes([]);
    };
  }, [selectedId]);

  if (selectedId !== undefined) {
    return (
      <Formik<DownloadImagesForm>
        initialValues={{
          image_types: imageTypes,
        }}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={async (values, formikActions) => {
          formikActions.setSubmitting(true);
          setLoading(true);
          const data = await DownloadFarmImagesApi.downloadImages({
            reportsId: selectedId,
            imageTypeIds: values.image_types
              .filter(imageType => imageType.value)
              .map(imageType => imageType.id)
              .join(","),
          });
          setLoading(false);
          formikActions.setSubmitting(false);
          if (data.data) {
            dispatch({ type: "CLOSE_RIGHT_POP_OUT" });
            handleToast(
              "Download triggered successfully. Please check your email."
            );
            setError(null);
          } else {
            setError("Error downloading images!");
          }
        }}
        render={({ values, handleSubmit, isSubmitting }) => (
          <>
            <div className="DownloadFarmImages">
              {/* Images to download */}
              {values.image_types.map((imageType, imageTypeIndex) => (
                <BoolCheckboxField
                  key={imageType.id}
                  name={`image_types[${imageTypeIndex}].value`}
                  label={imageType.label}
                  addMinWidth
                />
              ))}
              {/* Error Messages */}
              <div className="FormBox">
                <ErrorMessage name="image_types">
                  {message => <span className="ErrorMessage">{message}</span>}
                </ErrorMessage>
                {error && <span className="ErrorMessage">{error}</span>}
                {!loading && values.image_types.length === 0 && (
                  <span className="ErrorMessage">No images to download</span>
                )}
              </div>
            </div>
            <div className="Bottom">
              <button
                className="Btn BtnWhite"
                type="button"
                onClick={() => dispatch({ type: "CLOSE_RIGHT_POP_OUT" })}
              >
                Cancel
              </button>
              <button
                className={`Btn`}
                type="submit"
                onClick={() => handleSubmit()}
              >
                Download Images
              </button>
            </div>
            {(isSubmitting || loading) && (
              <span>
                <Spinner name="three-bounce" fadeIn="none" />
              </span>
            )}
          </>
        )}
      />
    );
  }
  return null;
};
